import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Card,
  Spin,
} from 'antd';
import {
  SettingFilled,
  CloudServerOutlined,
} from '@ant-design/icons';
import { Workspace } from '../../types';
import { useChatStore } from '../../store/chat';

interface WorkspaceToolsProps {
  workspace: Workspace;
}

export const WorkspaceTools: React.FC<WorkspaceToolsProps> = ({ workspace }) => {
  const { mcpServers, mcpMetadata, loadMCPData } = useChatStore();
  const [isLoadingMCP, setIsLoadingMCP] = useState(false);
  const [hoveredServer, setHoveredServer] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{x: number, y: number, isRightColumn: boolean} | null>(null);
  const iconRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  // Fallback metadata for when database is not available
  const fallbackMetadata = [
    {
      serverName: 'Outlook',
      displayName: 'Outlook',
      iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg',
      description: 'Access and manage your email, calendar, and contacts. Send emails, schedule meetings, search through your inbox, and stay organized with Microsoft Outlook integration.',
      category: 'communication'
    },
    {
      serverName: 'Teams',
      displayName: 'Microsoft Teams',
      iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg',
      description: 'Collaborate with your team through chat, video calls, and file sharing. Access Teams channels, send messages, schedule meetings, and coordinate projects seamlessly.',
      category: 'communication'
    },
    {
      serverName: 'Box',
      displayName: 'Box',
      iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/57/Box%2C_Inc._logo.svg',
      description: 'Store, share, and collaborate on files securely in the cloud. Upload, download, organize folders, share links, and manage document workflows with enterprise-grade security.',
      category: 'storage'
    },
    // Also add fallbacks for the old server names in case they're still being used
    {
      serverName: '365mail',
      displayName: 'Outlook',
      iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg',
      description: 'Access and manage your email, calendar, and contacts. Send emails, schedule meetings, search through your inbox, and stay organized with Microsoft Outlook integration.',
      category: 'communication'
    },
    {
      serverName: 'teams',
      displayName: 'Microsoft Teams',
      iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg',
      description: 'Collaborate with your team through chat, video calls, and file sharing. Access Teams channels, send messages, schedule meetings, and coordinate projects seamlessly.',
      category: 'communication'
    },
    {
      serverName: 'box',
      displayName: 'Box',
      iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/57/Box%2C_Inc._logo.svg',
      description: 'Store, share, and collaborate on files securely in the cloud. Upload, download, organize folders, share links, and manage document workflows with enterprise-grade security.',
      category: 'storage'
    }
  ];

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingMCP(true);
      try {
        await loadMCPData();
      } finally {
        setIsLoadingMCP(false);
      }
    };
    loadData();
  }, [loadMCPData]);

  const filteredServers = mcpServers.filter(server => 
    workspace.allowedMcpServers.includes(server.name)
  );

  const getServerMetadata = (serverName: string) => {
    // Try database metadata first, then fallback to hardcoded data
    return mcpMetadata.find(meta => meta.serverName === serverName) || 
           fallbackMetadata.find(meta => meta.serverName === serverName);
  };

  const handleMouseEnter = (serverName: string, index: number) => {
    const iconElement = iconRefs.current[serverName];
    if (iconElement) {
      const rect = iconElement.getBoundingClientRect();
      const isRightColumn = (index % 2) === 1;
      setPopoverPosition({
        x: rect.left + (isRightColumn ? rect.width : rect.width / 2),
        y: rect.bottom + 8,
        isRightColumn
      });
    }
    setHoveredServer(serverName);
  };

  const handleMouseLeave = () => {
    setHoveredServer(null);
    setPopoverPosition(null);
  };

  const isLoading = isLoadingMCP && mcpServers.length === 0;

  return (
    <>
    <Card style={{ 
      marginBottom: '1rem', 
      border: 'none', 
      boxShadow: 'none',
      backgroundColor: 'var(--color-bg-primary)',
      color: 'var(--color-text-primary)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <SettingFilled style={{ fontSize: '20px' }} />
        <h4 style={{ margin: 0, fontWeight: 'bold' }}>DATA SOURCES</h4>
      </div>
      
      {isLoading ? (
        <Spin tip="Loading data sources..." />
      ) : filteredServers.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem'
        }}>
          {filteredServers.map((server, index) => {
            const metadata = getServerMetadata(server.name);
            const isRightColumn = (index % 2) === 1; // Every second item is in the right column
            return (
              <div key={server.name} style={{ position: 'relative' }}>
                <div 
                  ref={(el) => { iconRefs.current[server.name] = el; }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: hoveredServer === server.name ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
                    transition: 'background-color 0.2s ease',
                    cursor: 'pointer',
                    aspectRatio: '1',
                    textAlign: 'center',
                    position: 'relative' as const
                  }}
                  onMouseEnter={() => handleMouseEnter(server.name, index)}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* 64x64 Icon Area - centered */}
                  <div style={{
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    marginBottom: '0.5rem'
                  }}>
                    {metadata?.iconUrl ? (
                      <img 
                        src={metadata.iconUrl}
                        alt={metadata.displayName || server.name}
                        style={{
                          width: '64px',
                          height: '64px',
                          objectFit: 'contain',
                          filter: server.connected ? 'none' : 'grayscale(100%)',
                          opacity: server.connected ? 1 : 0.5,
                        }}
                      />
                    ) : (
                      <CloudServerOutlined 
                        style={{ 
                          fontSize: '64px', 
                          color: server.connected 
                            ? 'var(--color-primary)' 
                            : 'var(--color-text-disabled)'
                        }} 
                      />
                    )}
                    {/* Connection status indicator */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-4px',
                      right: '-4px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: server.connected 
                        ? 'var(--color-success)' 
                        : 'var(--color-error)',
                      border: '2px solid var(--color-bg-secondary)',
                    }} />
                  </div>
                  
                  {/* Service Name - only show name */}
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    lineHeight: '1.1'
                  }}>
                    {metadata?.displayName || server.name}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem',
          color: 'var(--color-text-secondary)'
        }}>
          <CloudServerOutlined style={{ fontSize: '48px', marginBottom: '1rem' }} />
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            No data sources are currently available.
          </p>
        </div>
      )}
    </Card>
    
    {/* Portal-based popover that renders outside the container */}
    {hoveredServer && popoverPosition && (
      createPortal(
        <div
          style={{
            position: 'fixed',
            top: `${popoverPosition.y}px`,
            ...(popoverPosition.isRightColumn ? {
              right: `${window.innerWidth - popoverPosition.x}px`,
            } : {
              left: `${popoverPosition.x}px`,
              transform: 'translateX(-50%)',
            }),
            zIndex: 99999,
            backgroundColor: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: '6px',
            padding: '0.75rem',
            width: '320px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary)',
            lineHeight: '1.5',
            textAlign: 'left',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            minHeight: 'auto',
            pointerEvents: 'none', // Prevent interference with hover
          }}
        >
          {(() => {
            const metadata = getServerMetadata(hoveredServer);
            return metadata ? (
              <>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '0.5rem',
                  color: 'var(--color-text-primary)'
                }}>
                  {metadata.displayName}
                </div>
                {metadata.description}
              </>
            ) : null;
          })()}
        </div>,
        document.body
      )
    )}
  </>);
};